import {
    Cancel,
    Check,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TableRow,
    Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { useContext, useState } from "react";
import { Material, MaterialBatch } from "../../../../data/entities";
import { ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import PageLoader from "../../../../shared/components/LoadingPage";
import TextBox from "../../../../shared/components/TextBox";
import { api } from "../../../../shared/hooks/api";
import { ContentWrapper } from "../../../../shared/styled/TableStyles";
import { color } from "../../../../shared/styles";
import Edit from "../../../../shared/svg/Edit";
import apiMethods from "../../../../utils/api";
import {
    formatInternationalNumber,
    formatNumber,
    parseInternationalToNumber,
} from "../../../../utils/format";
import { error, success, warning } from "../../../../utils/toast";
import { TableItemCounter, TableTitle } from "../MaterialList/Styled";

const DATE_PICKER_SX = {
    width: "156px",
    "& .MuiInputBase-root": {
        height: "36px",
        fontSize: "13px",
        fontFamily: "Noto Sans",
    },
    "& .MuiInputBase-input": {
        padding: "7px 8px",
    },
};

const MaterialExpirationManagerTableColumns = [
    { text: "Ngày đến hạn", icon: <></> },
    { text: "Tồn thực tế lô", icon: <></> },
    { text: "Khóa thủ công", icon: <></> },
    { text: "Đang khóa", icon: <></> },
    { text: "Khả dụng", icon: <></> },
    { text: "Trạng thái", icon: <></> },
    { text: "", icon: <></> },
];

export const canEditMaterialBatch = (
    role: ROLE,
    materialStorage: string,
) => {
    if (role === ROLE.MANAGER) return true;
    if (role === ROLE.FIRST_STORAGE_KEEPER) return materialStorage === "Kho 1";
    if (role === ROLE.SECOND_STORAGE_KEEPER) return materialStorage === "Kho 2";
    return false;
};

const getBatchStatus = (item: MaterialBatch) => {
    if (item.is_expired) return "Đã hết hạn";
    if (!item.due_date) return "Vô thời hạn";
    if (dayjs(item.due_date).diff(dayjs(), "day") <= 30) return "Sắp hết hạn";
    return "Còn hạn";
};

const getStatusBackground = (item: MaterialBatch) => {
    const status = getBatchStatus(item);
    if (status === "Đã hết hạn") return color.status.DANGER_LIGHT;
    if (status === "Sắp hết hạn") return color.status.WARNING_LIGHT;
    return "transparent";
};

interface MaterialExpirationManagerRowProps {
    item: MaterialBatch;
    extraRowProps: {
        materialId: string;
        canEdit: boolean;
        fetchMaterialBatches: () => unknown;
        fetchMaterial: () => unknown;
    };
}

const MaterialExpirationManagerRow = ({
    item,
    extraRowProps,
}: MaterialExpirationManagerRowProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [lockedQuantity, setLockedQuantity] = useState(item.locked_quantity);
    const [dueDate, setDueDate] = useState<Dayjs | null>(
        item.due_date ? dayjs(item.due_date) : null,
    );
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const originalDueDate = item.due_date ? dayjs(item.due_date) : null;
    const dueDateChanged =
        (originalDueDate?.format("YYYY-MM-DD") ?? null)
        !== (dueDate?.format("YYYY-MM-DD") ?? null);

    const resetEditing = () => {
        setLockedQuantity(item.locked_quantity);
        setDueDate(item.due_date ? dayjs(item.due_date) : null);
        setIsEditing(false);
        setIsConfirmOpen(false);
    };

    const saveChanges = async () => {
        if (lockedQuantity < 0 || lockedQuantity > item.quantity) {
            warning(
                "Số lượng khóa không hợp lệ",
                `Giá trị phải nằm trong khoảng 0-${formatNumber(item.quantity)}.`,
            );
            return;
        }

        const payload: Record<string, number | string | null> = {
            locked_quantity: lockedQuantity,
        };

        payload.due_date = dueDate ? dueDate.format("YYYY-MM-DD") : null;


        setIsSaving(true);
        try {
            await apiMethods.patch(
                `/materials/${extraRowProps.materialId}/batches/${item.id}/`,
                payload,
            );
            await Promise.all([
                extraRowProps.fetchMaterialBatches(),
                extraRowProps.fetchMaterial(),
            ]);
            success("THÀNH CÔNG", "Cập nhật lô vật tư thành công");
            resetEditing();
        } catch (err) {
            error("Cập nhật lô vật tư thất bại", err);
        } finally {
            setIsSaving(false);
            setIsConfirmOpen(false);
        }
    };

    const handleSave = () => {
        const opensExpiredBatch =
            item.is_expired
            && dueDateChanged
            && dueDate
            && dueDate.isAfter(dayjs(), "day");
        if (opensExpiredBatch) {
            setIsConfirmOpen(true);
            return;
        }
        void saveChanges();
    };

    return (
        <>
            <TableRow sx={{ backgroundColor: `${getStatusBackground(item)} !important` }}>
                <StyledTableCell align="left">
                    {isEditing ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
                            <DatePicker
                                value={dueDate}
                                onChange={setDueDate}
                                format="DD/MM/YYYY"
                                // maxDate={
                                //     item.max_extendable_due_date
                                //         ? dayjs(item.max_extendable_due_date)
                                //         : undefined
                                // }
                                // slotProps={{
                                //     textField: {
                                //         placeholder: "DD/MM/YYYY",
                                //         helperText: item.max_extendable_due_date
                                //             ? `Tối đa ${dayjs(item.max_extendable_due_date).format("DD/MM/YYYY")}`
                                //             : undefined,
                                //     },
                                // }}
                                sx={DATE_PICKER_SX}
                            />
                        </LocalizationProvider>
                    ) : (
                        item.due_date
                            ? dayjs(item.due_date).format("DD/MM/YYYY")
                            : "Vô thời hạn"
                    )}
                </StyledTableCell>
                <StyledTableCell align="left">
                    {formatNumber(item.quantity)}
                </StyledTableCell>
                <StyledTableCell align="left">
                    {isEditing ? (
                        <TextBox
                            numeric
                            text={formatInternationalNumber(lockedQuantity ?? "")}
                            onChangeText={(value: string) => {
                                const parsed = parseInternationalToNumber(value, error);
                                if (Number.isFinite(parsed)) setLockedQuantity(parsed);
                            }}
                            style={{ width: "112px" }}
                        />
                    ) : (
                        formatNumber(item.locked_quantity)
                    )}
                </StyledTableCell>
                <StyledTableCell align="left">
                    {formatNumber(item.effective_locked_quantity)}
                </StyledTableCell>
                <StyledTableCell align="left">
                    {formatNumber(item.available_quantity)}
                </StyledTableCell>
                <StyledTableCell align="left">
                    {getBatchStatus(item)}
                </StyledTableCell>
                <StyledTableCell align="left">
                    {extraRowProps.canEdit && (
                        isEditing ? (
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                <IconButton
                                    aria-label="Lưu thay đổi lô vật tư"
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    <Check />
                                </IconButton>
                                <IconButton
                                    aria-label="Hủy chỉnh sửa lô vật tư"
                                    onClick={resetEditing}
                                    disabled={isSaving}
                                >
                                    <Cancel />
                                </IconButton>
                            </Box>
                        ) : (
                            <IconButton
                                aria-label="Chỉnh sửa lô vật tư"
                                onClick={() => setIsEditing(true)}
                            >
                                <Edit />
                            </IconButton>
                        )
                    )}
                </StyledTableCell>
            </TableRow>

            <Dialog
                open={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Xác nhận gia hạn lô vật tư</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sau khi gia hạn, tối đa{" "}
                        <strong>
                            {formatNumber(
                                Math.max(item.quantity - lockedQuantity, 0),
                            )}
                        </strong>{" "}
                        đơn vị sẽ có thể sử dụng lại. Số lượng khóa thủ công vẫn
                        được giữ nguyên.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmOpen(false)}>
                        Quay lại
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void saveChanges()}
                        disabled={isSaving}
                    >
                        Xác nhận gia hạn
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

interface MaterialExpirationManagerProps {
    material: Material;
    fetchMaterial: () => unknown;
}

const StockMetric = ({ label, value }: { label: string; value: number }) => (
    <Box
        sx={{
            minWidth: 0,
            px: 2,
            py: 1.5,
            borderRight: { xs: "none", md: `1px solid ${color.secondary.GREY_4}` },
            "&:last-child": { borderRight: "none" },
        }}
    >
        <Typography sx={{ fontSize: 12, color: color.emphasis.NEUTRAL_N70 }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: "Noto Sans" }}>
            {formatNumber(value)}
        </Typography>
    </Box>
);

const MaterialExpirationManager = ({
    material,
    fetchMaterial,
}: MaterialExpirationManagerProps) => {
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const [
        { data: materialBatches, isLoading },
        fetchMaterialBatches,
    ] = api.get<MaterialBatch[]>(`/materials/${material.id}/batches/`);

    if (isLoading) return <PageLoader />;

    const batches = materialBatches || [];
    const canEdit = canEditMaterialBatch(role, material.storage);

    return (
        <ContentWrapper>
            <TableTitle>
                Quản lý ngày đến hạn
                <TableItemCounter>
                    {" "}
                    ({batches.length} nhóm ngày đến hạn)
                </TableItemCounter>
            </TableTitle>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    borderTop: `1px solid ${color.secondary.GREY_4}`,
                    borderBottom: `1px solid ${color.secondary.GREY_4}`,
                    mb: 1,
                }}
            >
                <StockMetric label="Tồn thực tế" value={material.quantity ?? 0} />
                <StockMetric label="Đang khóa" value={material.locked_quantity ?? 0} />
                <StockMetric
                    label="Khả dụng"
                    value={material.available_quantity ?? material.quantity ?? 0}
                />
            </Box>

            <CustomTable
                itemList={batches}
                RowElement={MaterialExpirationManagerRow}
                extraRowProps={{
                    materialId: material.id,
                    canEdit,
                    fetchMaterialBatches,
                    fetchMaterial,
                }}
                TableColumns={MaterialExpirationManagerTableColumns}
            />
        </ContentWrapper>
    );
};

export default MaterialExpirationManager;
